import { Box, BoxProps, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import ReactPlayerImport, { ReactPlayerProps } from "react-player";
import { GameMedia } from "../../client";
import { defaultStreamUrl } from "../../utils/getStreamUrl";
import { thumbHashDataUrl } from "../../utils/thumbHash";
import { PivoImage } from "./PivoImage";
import { Video } from "./Player/Video";

// react-player ships transpiled CJS; when Node loads it externally during SSR
// the default import is the whole exports object instead of the component.
const ReactPlayer = ((ReactPlayerImport as any).default ?? ReactPlayerImport) as typeof ReactPlayerImport;

interface IContentViewerProps {
    media: GameMedia;
    autoplay?: boolean;
    videoProps?: ReactPlayerProps;
    onNext?: () => void;
    onPrevious?: () => void;
}

export function ContentViewer({ media, autoplay, videoProps, onPrevious, onNext }: IContentViewerProps) {
    const { t } = useTranslation();
    if (media.mediaType) {
        switch (media.mediaType.split("/")[0]) {
            case "video":
                const videoSources = { original: defaultStreamUrl(media.attachmentId) };
                const subtitleSources = {};

                return (
                    <Video
                        videoProps={videoProps}
                        sources={videoSources}
                        subtitles={subtitleSources}
                        minW="100%"
                        maxW="100%"
                        minH="100%"
                        maxH="100%"
                        autoplay={autoplay}
                        onPrevious={onPrevious}
                        onNext={onNext}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                    />
                );
            case "image":
                return <ContentImage media={media} />;
        }
    }

    return <Text color="gray.500">{t("content.unknownType").toString()}</Text>;
}

function ContentImage({ media }: { media: GameMedia }) {
    const placeholder = useMemo(() => thumbHashDataUrl(media.thumbHash), [media.thumbHash]);

    return (
        <img
            src={defaultStreamUrl(media.attachmentId)}
            alt={""}
            style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                objectPosition: "center center",
                backgroundImage: placeholder ? `url(${placeholder})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        />
    );
}

export function ContentPreview({ media, autoplay, ...props }: IContentViewerProps & BoxProps) {
    const { t } = useTranslation();
    return (
        <Box className="cover-video" h="100%" w="100%" {...props}>
            {(() => {
                if (media.mediaType) {
                    switch (media.mediaType.split("/")[0]) {
                        case "video":
                            const videoSource = defaultStreamUrl(media.attachmentId);

                            return (
                                <Box
                                    display="grid"
                                    gridTemplateColumns="1fr"
                                    gridTemplateRows="1fr"
                                    width="100%"
                                    height="100%"
                                >
                                    <PivoImage
                                        id={media.attachmentId}
                                        thumbHash={media.thumbHash}
                                        sizes="25vw"
                                        width="100%"
                                        height="100%"
                                        gridColumnStart={1}
                                        gridRowStart={1}
                                        imageProps={{
                                            style: {
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                                objectPosition: "center center",
                                            },
                                        }}
                                    />
                                    <ReactPlayer
                                        width={"100%"}
                                        height={"100%"}
                                        style={{
                                            gridColumnStart: 1,
                                            gridRowStart: 1,
                                        }}
                                        playing={autoplay ?? true}
                                        volume={0}
                                        muted={true}
                                        url={videoSource}
                                        config={{
                                            file: {
                                                attributes: {
                                                    crossorigin: "use-credentials",
                                                },
                                            },
                                        }}
                                        loop={true}
                                    />
                                </Box>
                            );
                        case "image":
                            return (
                                <PivoImage
                                    id={media.attachmentId}
                                    thumbHash={media.thumbHash}
                                    sizes="25vw"
                                    width="100%"
                                    height="100%"
                                    imageProps={{
                                        style: {
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            objectPosition: "center center",
                                        },
                                    }}
                                />
                            );
                    }
                }

                return <Text color="gray.500">{t("content.unknownType").toString()}</Text>;
            })()}
        </Box>
    );
}
